import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatNumber, numberWithCommas } from "../../utils";
import { QrTrendBucket } from "../../types/qr-analytics.type";
import {
  CASH_CHANNEL,
  CHART_GRID,
  CHART_INK,
  CREDIT_CHANNEL,
  DIGITAL_CHANNELS,
  DIGITAL_GROUP,
  QR_CHANNELS,
  formatBucketLabel,
} from "./channel";
import BucketTick, { SUNDAY_INK, isSundayBucket } from "./BucketTick";

export type ChannelMeasure = "bill_count" | "amount";

interface ChannelChartProps {
  readonly buckets: QrTrendBucket[];
  /** Bills or baht. Two measures of different scale get two views, never two y-axes. */
  readonly measure: ChannelMeasure;
}

interface ChartRow {
  bucket: string;
  label: string;
  cash: number;
  transfer: number;
  qr: number;
  credit: number;
  total: number;
  digital_percent: number;
}

/**
 * เงินสด / เงินโอน / QR per day or month, stacked.
 *
 * Stacked rather than grouped because the question is "how does the bill population divide",
 * and the total height is itself meaningful (how busy the period was). The stack order is the
 * fixed channel order — cash at the base, then เงินโอน and QR, which is also what keeps the
 * เงินโอน+QR group contiguous on top of every bar. The legend and tooltip name the group; the
 * bars stay three colours so the group costs no extra hue.
 *
 * Segments carry a 2px white stroke so two fills never touch. The QR share is deliberately
 * NOT overlaid here as a second y-axis: it lives in its own chart below.
 */
export default function ChannelChart({ buckets, measure }: ChannelChartProps) {
  const data = useMemo<ChartRow[]>(
    () =>
      buckets.map((bucket) => ({
        bucket: bucket.bucket,
        label: formatBucketLabel(bucket.bucket),
        cash:
          measure === "bill_count"
            ? bucket.cash_count
            : Number(bucket.cash_amount),
        transfer:
          measure === "bill_count"
            ? bucket.transfer_count
            : Number(bucket.transfer_amount),
        qr:
          measure === "bill_count" ? bucket.qr_count : Number(bucket.qr_amount),
        credit:
          measure === "bill_count"
            ? bucket.credit_count
            : Number(bucket.credit_amount),
        total:
          measure === "bill_count"
            ? bucket.bill_count
            : Number(bucket.total_amount),
        digital_percent: bucket.digital_percent,
      })),
    [buckets, measure],
  );

  const showCredit = useMemo(() => data.some((row) => row.credit > 0), [data]);
  const bucketKeys = useMemo(() => data.map((row) => row.bucket), [data]);
  const unit = measure === "bill_count" ? "บิล" : "บาท";

  if (data.length === 0) {
    return <EmptyChart />;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={CHART_GRID} vertical={false} />
        <XAxis
          dataKey="label"
          tick={<BucketTick buckets={bucketKeys} />}
          axisLine={{ stroke: CHART_GRID }}
          tickLine={false}
          interval="preserveStartEnd"
          minTickGap={16}
        />
        <YAxis
          tick={{ fill: CHART_INK, fontSize: 12 }}
          axisLine={false}
          tickLine={false}
          width={64}
          tickFormatter={(value: number) => numberWithCommas(value)}
        />
        <Tooltip
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const row = payload[0].payload as ChartRow;
            const sunday = isSundayBucket(row.bucket);
            return (
              <Box
                sx={{
                  bgcolor: "#fff",
                  border: `1px solid ${CHART_GRID}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: sunday ? SUNDAY_INK : undefined }}
                >
                  {label}
                  {sunday ? " (อาทิตย์)" : ""}
                </Typography>
                {[
                  {
                    label: CASH_CHANNEL.label,
                    color: CASH_CHANNEL.color as string | undefined,
                    value: row.cash,
                    kind: "channel",
                  },
                  {
                    label: DIGITAL_GROUP.label,
                    color: undefined,
                    value: row.transfer + row.qr,
                    kind: "group",
                  },
                  ...DIGITAL_CHANNELS.map((channel) => ({
                    label: channel.label,
                    color: channel.color as string | undefined,
                    value: row[channel.key as "transfer" | "qr"],
                    kind: "member",
                  })),
                  ...(showCredit
                    ? [
                        {
                          label: CREDIT_CHANNEL.label,
                          color: CREDIT_CHANNEL.color as string | undefined,
                          value: row.credit,
                          kind: "channel",
                        },
                      ]
                    : []),
                ].map((entry) => (
                  <Box
                    key={entry.label}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    pl={entry.kind === "member" ? 1.5 : 0}
                  >
                    {entry.color && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "2px",
                          bgcolor: entry.color,
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color={
                        entry.kind === "group" ? undefined : "text.secondary"
                      }
                      fontWeight={entry.kind === "group" ? 600 : undefined}
                      sx={{
                        color:
                          entry.kind === "group"
                            ? DIGITAL_GROUP.color
                            : undefined,
                      }}
                    >
                      {entry.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={entry.kind === "group" ? 600 : undefined}
                      sx={{ ml: "auto" }}
                    >
                      {formatNumber(entry.value, {
                        decimals: measure === "bill_count" ? 0 : 2,
                      })}
                    </Typography>
                  </Box>
                ))}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  รวม{" "}
                  {formatNumber(row.total, {
                    decimals: measure === "bill_count" ? 0 : 2,
                  })}{" "}
                  {unit} · {DIGITAL_GROUP.label}{" "}
                  {formatNumber(row.digital_percent)}%
                </Typography>
              </Box>
            );
          }}
        />
        <Legend
          verticalAlign="bottom"
          height={32}
          content={() => <GroupedLegend showCredit={showCredit} />}
        />
        {QR_CHANNELS.map((channel, index) => (
          <Bar
            key={channel.key}
            dataKey={channel.key}
            name={channel.label}
            stackId="channel"
            fill={channel.color}
            stroke="#fff"
            strokeWidth={2}
            radius={
              !showCredit && index === QR_CHANNELS.length - 1
                ? [4, 4, 0, 0]
                : undefined
            }
            maxBarSize={48}
          />
        ))}
        {showCredit && (
          <Bar
            dataKey="credit"
            name={CREDIT_CHANNEL.label}
            stackId="channel"
            fill={CREDIT_CHANNEL.color}
            stroke="#fff"
            strokeWidth={2}
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * เงินสด, then เงินโอน+QR as one pill holding its two members — the legend is where the
 * grouping is named on this chart, since the bars themselves carry no group mark.
 */
function GroupedLegend({ showCredit }: { readonly showCredit: boolean }) {
  const item = (color: string, label: string) => (
    <Box key={label} display="inline-flex" alignItems="center" gap={0.75}>
      <Box
        sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color }}
      />
      <span style={{ color: CHART_INK, fontSize: 12 }}>{label}</span>
    </Box>
  );
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexWrap="wrap"
      gap={2}
      pt={1}
    >
      {item(CASH_CHANNEL.color, CASH_CHANNEL.label)}
      <Box
        display="inline-flex"
        alignItems="center"
        gap={1.5}
        sx={{
          border: `1px solid ${CHART_GRID}`,
          borderRadius: 3,
          px: 1.25,
          py: 0.25,
        }}
      >
        <span
          style={{ color: DIGITAL_GROUP.color, fontSize: 12, fontWeight: 600 }}
        >
          {DIGITAL_GROUP.label}
        </span>
        {DIGITAL_CHANNELS.map((channel) => item(channel.color, channel.label))}
      </Box>
      {showCredit && item(CREDIT_CHANNEL.color, CREDIT_CHANNEL.label)}
    </Box>
  );
}

export function EmptyChart() {
  return (
    <Box
      sx={{
        height: 280,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        ไม่มีข้อมูลในช่วงที่เลือก
      </Typography>
    </Box>
  );
}
